import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  FileText, Clock, CheckCircle, XCircle, Plus, Trophy,
  Calendar, Crown, Users, ScrollText, Sparkles, Star,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NominationsPage() {
  const { isAuthenticated } = useAuth();
  const [nominations, setNominations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchNominations();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchNominations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/nominations`, { withCredentials: true });
      setNominations(response.data);
    } catch (error) {
      toast.error('Failed to load nominations');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { icon: Clock, color: 'text-yellow-500 bg-yellow-500/10', label: 'Pending' },
      approved: { icon: CheckCircle, color: 'text-green-500 bg-green-500/10', label: 'Approved' },
      rejected: { icon: XCircle, color: 'text-red-500 bg-red-500/10', label: 'Rejected' }
    };
    const { icon: Icon, color, label } = config[status] || config.pending;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm ${color}`}>
        <Icon className="w-4 h-4" />
        {label}
      </span>
    );
  };

  return (
    <main className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8" data-testid="nominations-page">
      <div className="max-w-5xl mx-auto">
        <NominationIntro onSubmit={() => setShowForm(true)} isAuthenticated={isAuthenticated} />

        {/* Submit form + my-nominations section — auth-gated */}
        {isAuthenticated && (
        <section className="mt-20" data-testid="nominations-list-section">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80 mb-2">
                Your Submissions
              </p>
              <h2 className="font-['Playfair_Display'] text-3xl font-medium text-white">
                My Nominations
              </h2>
            </div>
            <Dialog open={showForm} onOpenChange={setShowForm}>
              <DialogTrigger asChild>
                <Button
                  className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A] h-12 px-6"
                  data-testid="submit-nomination-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Submit Nomination
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121212] border-white/10 max-w-xl">
                <DialogHeader>
                  <DialogTitle className="text-white font-['Playfair_Display'] text-xl">
                    Submit a Nomination
                  </DialogTitle>
                </DialogHeader>
                <NominationForm
                  onSuccess={() => {
                    setShowForm(false);
                    fetchNominations();
                  }}
                  onCancel={() => setShowForm(false)}
                />
              </DialogContent>
            </Dialog>
          </div>

        {/* Nominations List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 skeleton rounded-xl" />
            ))}
          </div>
        ) : nominations.length === 0 ? (
          <div className="text-center py-20 bg-[#121212] rounded-2xl border border-white/5">
            <FileText className="w-16 h-16 mx-auto text-white/20 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No nominations yet</h3>
            <p className="text-white/50 mb-6">
              Be the first to nominate an exceptional travel experience
            </p>
            <Button 
              onClick={() => setShowForm(true)}
              className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Submit Nomination
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {nominations.map((nomination) => (
              <div
                key={nomination.id}
                className="p-6 bg-[#121212] rounded-xl border border-white/5"
                data-testid={`nomination-item-${nomination.id}`}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">
                      {nomination.name}
                    </h3>
                    <p className="text-white/50 text-sm">
                      {nomination.location}, {nomination.country} • {nomination.category}
                    </p>
                  </div>
                  {getStatusBadge(nomination.status)}
                </div>

                <p className="text-white/70 text-sm mb-4">
                  {nomination.description}
                </p>

                <div className="bg-[#0A0A0A] p-4 rounded-lg">
                  <p className="text-xs text-white/40 uppercase tracking-wider mb-1">
                    Reason for nomination
                  </p>
                  <p className="text-white/80 text-sm">
                    {nomination.reason}
                  </p>
                </div>

                <p className="text-white/30 text-xs mt-4">
                  Submitted on {new Date(nomination.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        )}
        </section>
        )}
      </div>
    </main>
  );
}

function NominationForm({ onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    category: 'hotel',
    description: '',
    location: '',
    country: '',
    reason: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/nominations`, formData, { withCredentials: true });
      toast.success('Nomination submitted successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit nomination');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="text-white/70">Name of Hotel/Resort/Destination</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          placeholder="e.g., The Grand Hotel"
          className="bg-[#0A0A0A] border-white/10 text-white"
          data-testid="nomination-name"
        />
      </div>

      <div>
        <Label className="text-white/70">Category</Label>
        <Select
          value={formData.category}
          onValueChange={(value) => setFormData({ ...formData, category: value })}
        >
          <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white" data-testid="nomination-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10">
            <SelectItem value="hotel">Hotel</SelectItem>
            <SelectItem value="resort">Resort</SelectItem>
            <SelectItem value="destination">Destination</SelectItem>
            <SelectItem value="air">Air</SelectItem>
            <SelectItem value="experience">Experience</SelectItem>
            <SelectItem value="b2b">B2B</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-white/70">Location</Label>
          <Input
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            required
            placeholder="City"
            className="bg-[#0A0A0A] border-white/10 text-white"
            data-testid="nomination-location"
          />
        </div>
        <div>
          <Label className="text-white/70">Country</Label>
          <Input
            value={formData.country}
            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
            required
            placeholder="Country"
            className="bg-[#0A0A0A] border-white/10 text-white"
            data-testid="nomination-country"
          />
        </div>
      </div>

      <div>
        <Label className="text-white/70">Description</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          required
          rows={3}
          placeholder="Describe the hotel, resort, or destination..."
          className="bg-[#0A0A0A] border-white/10 text-white resize-none"
          data-testid="nomination-description"
        />
      </div>

      <div>
        <Label className="text-white/70">Why should it be recognized?</Label>
        <Textarea
          value={formData.reason}
          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
          required
          rows={3}
          placeholder="Tell us why this place deserves recognition..."
          className="bg-[#0A0A0A] border-white/10 text-white resize-none"
          data-testid="nomination-reason"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="border-white/10 text-white">
          Cancel
        </Button>
        <Button type="submit" disabled={loading} className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A]" data-testid="nomination-submit">
          {loading ? 'Submitting...' : 'Submit Nomination'}
        </Button>
      </div>
    </form>
  );
}

// ============== INFORMATIONAL INTRO ==============

const WHY_NOMINATE = [
  {
    emoji: '🌍',
    title: '1. Gain Global Recognition',
    body: "Position your brand among the world's leading travel, tourism, hospitality, and luxury organizations. A nomination demonstrates your commitment to excellence and helps increase international visibility.",
  },
  {
    emoji: '🏆',
    title: '2. Enhance Brand Credibility',
    body: 'Being recognized by an independent awards program strengthens trust with travelers, clients, partners, and industry stakeholders. Award recognition serves as a valuable mark of quality and achievement.',
  },
  {
    emoji: '📈',
    title: '3. Increase Marketing & PR Exposure',
    body: 'Nominees and winners benefit from promotional opportunities across award platforms, media channels, social media, newsletters, and industry publications, helping to attract new customers and business opportunities.',
  },
  {
    emoji: '⭐',
    title: '4. Showcase Your Excellence',
    body: 'Highlight your achievements, innovation, service quality, guest satisfaction, sustainability initiatives, and unique experiences that set your business apart from competitors.',
  },
  {
    emoji: '🤝',
    title: '5. Strengthen Industry Reputation',
    body: 'Join a prestigious community of respected travel brands, destinations, hospitality leaders, and tourism professionals while building valuable industry connections and partnerships.',
  },
  {
    emoji: '🚀',
    title: "6. Celebrate Your Team's Success",
    body: 'Recognize the hard work, passion, and dedication of your employees and leadership team. A nomination is an opportunity to reward excellence and inspire future growth and innovation.',
  },
];

const ELIGIBLE = [
  'Luxury Hotels & Resorts', 'Boutique Hotels & Villas', 'Hotel Groups & Collections',
  'Travel Agencies & Tour Operators', 'Destination Management Companies',
  'Airlines & Aviation Services', 'Cruise Lines & Yacht Experiences',
  'Tourism Boards & Destinations', 'Travel Technology Companies',
  'Luxury Travel Brands', 'Hospitality Professionals',
];

const PROCESS_STEPS = [
  { n: '01', title: 'Submit Your Nomination', body: 'Complete the online nomination form and select the categories most relevant to your organization.' },
  { n: '02', title: 'Eligibility Review',     body: 'Our awards team reviews submissions to ensure nominees meet category requirements and industry standards.' },
  { n: '03', title: 'Evaluation & Voting',    body: 'Eligible nominees may be assessed through a combination of Public Voting, Professional Partner Evaluation, Independent Jury Assessment, and Administrative Verification.' },
  { n: '04', title: 'Final Scoring',          body: 'All results are normalized and independently reviewed to ensure fairness and transparency.' },
  { n: '05', title: 'Winners Announcement',   body: 'Gold, Silver, and Bronze winners will be announced through the official awards platform and global media channels.' },
];

const LEVELS = [
  { tier: 'Gold Award',   range: '90 – 100',   color: 'bg-[#D4AF37] text-[#0A0A0A]', ring: 'ring-[#D4AF37]/40' },
  { tier: 'Silver Award', range: '80 – 89.99', color: 'bg-[#C0C0C0] text-[#0A0A0A]', ring: 'ring-[#C0C0C0]/30' },
  { tier: 'Bronze Award', range: '70 – 79.99', color: 'bg-[#CD7F32] text-white',     ring: 'ring-[#CD7F32]/30' },
];

const KEY_DATES = [
  { label: 'Nominations Open',                     date: 'Now' },
  { label: 'Nominations Close',                    date: 'February 28, 2027' },
  { label: 'Public Voting Period',                 date: 'March 1 – April 18, 2027' },
  { label: 'Professional Partner Voting Period',   date: 'March 1 – April 18, 2027' },
  { label: 'Finalists Announced',                  date: 'April 19, 2027' },
  { label: 'Jury Evaluation',                      date: 'April 19 – 25, 2027' },
  { label: 'Winners Announcement',                 date: 'April 27, 2027' },
];

function NominationIntro({ onSubmit, isAuthenticated = false }) {
  // For logged-out visitors, the "Submit" buttons should send them to register first.
  const handleSubmitClick = () => {
    if (isAuthenticated) {
      onSubmit();
    } else {
      window.location.href = '/register?next=/nominate';
    }
  };
  return (
    <div className="space-y-20" data-testid="nomination-intro">
      {/* Hero */}
      <section className="text-center">
        <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80 mb-4 flex items-center justify-center gap-2">
          <Crown className="w-4 h-4" />
          Nominate for the 2027 Travel Awards & TWBL Awards
        </p>
        <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-medium text-white mb-6 leading-tight">
          Excellence Deserves
          <br />
          <span className="text-gradient-gold">Global Recognition</span>
        </h1>
        <div className="text-white/60 max-w-3xl mx-auto leading-relaxed text-base sm:text-lg space-y-4">
          <p>
            Every unforgettable journey begins with exceptional people, visionary brands,
            and remarkable destinations. The 2027 Travel Awards & TWBL Awards (The
            World&apos;s Best Luxury Awards) celebrate the organizations and professionals
            setting new standards in travel, tourism, hospitality, luxury lifestyle, and
            destination excellence.
          </p>
          <p>
            Whether you are a luxury hotel, boutique resort, destination management
            company, tourism board, travel agency, airline, cruise operator, villa
            collection, or travel technology provider — this is your opportunity to
            showcase your achievements on a global stage.
          </p>
          <p>
            Our awards program recognizes innovation, service excellence, guest
            satisfaction, sustainability, and outstanding contributions to the travel
            industry through a transparent nomination and evaluation process inspired by
            leading international travel awards.
          </p>
        </div>
        <Button
          onClick={handleSubmitClick}
          className="mt-10 bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A] h-12 px-8 text-base"
          data-testid="hero-nominate-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          {isAuthenticated ? 'Submit Your Nomination' : 'Sign Up to Nominate'}
        </Button>
      </section>

      {/* Free Nomination Reassurance */}
      <section className="-mt-6">
        <div
          className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#1A1408] via-[#0F0F0F] to-[#0A0A0A] p-8 sm:p-10 text-center"
          data-testid="free-nomination-banner"
        >
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl" />
          <div className="relative">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D4AF37] text-[#0A0A0A] text-[10px] font-bold uppercase tracking-[0.18em] mb-5 shadow-[0_0_12px_rgba(212,175,55,0.4)]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0A0A0A] animate-pulse" />
              Free Nominations
            </span>
            <h3 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-white mb-2">
              No submission fee — nominate at no cost
            </h3>
            <p className="text-white/60 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
              We&apos;ve made nomination free for the 2027 Travel Awards & TWBL Awards. Submit
              your organization across Hotel · Resort · Villa, Air · Cruise · B2B, or
              Destination categories without any payment.
            </p>
          </div>
        </div>
      </section>

      {/* Why Nominate */}
      <section>
        <SectionHeader icon={Sparkles} eyebrow="6 Powerful Reasons" title="Why Nominate?" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {WHY_NOMINATE.map((w) => (
            <div
              key={w.title}
              className="p-6 rounded-2xl bg-[#121212] border border-white/5 hover:border-[#D4AF37]/30 transition-all"
              data-testid={`why-${w.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 text-2xl leading-none" aria-hidden>
                {w.emoji}
              </div>
              <h3 className="font-['Playfair_Display'] text-xl text-white mb-2">{w.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{w.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Eligible */}
      <section>
        <SectionHeader icon={Users} eyebrow="Open To" title="Who Can Be Nominated?" />
        <p className="text-white/60 mb-8 max-w-3xl">
          Nominations are open worldwide. Self-nominations and third-party nominations
          are welcome — businesses, customers, travel advisors, partners, and industry
          professionals may all submit.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {ELIGIBLE.map((e) => (
            <div
              key={e}
              className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#121212] border border-white/5"
            >
              <CheckCircle className="w-4 h-4 text-[#D4AF37] shrink-0" />
              <span className="text-white/80 text-sm">{e}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section>
        <SectionHeader icon={ScrollText} eyebrow="Workflow" title="How the Awards Process Works" />
        <div className="space-y-4">
          {PROCESS_STEPS.map((s) => (
            <div
              key={s.n}
              className="flex gap-5 p-6 rounded-2xl bg-[#121212] border border-white/5 hover:border-[#D4AF37]/30 transition-all"
              data-testid={`step-${s.n}`}
            >
              <span className="font-['Playfair_Display'] text-3xl text-[#D4AF37]/60 shrink-0 leading-none">
                {s.n}
              </span>
              <div>
                <h3 className="font-['Playfair_Display'] text-xl text-white mb-1">{s.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Levels */}
      <section>
        <SectionHeader icon={Trophy} eyebrow="Award Recognition Levels" title="Score thresholds — no forced ranking" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {LEVELS.map((l) => (
            <div
              key={l.tier}
              className={`p-7 rounded-2xl bg-[#121212] border border-white/5 ring-1 ${l.ring} text-center`}
            >
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.2em] mb-5 ${l.color}`}>
                <Trophy className="w-3.5 h-3.5" />
                {l.tier}
              </div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Score Range</p>
              <p className="font-['Playfair_Display'] text-2xl text-white">{l.range}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-white/50 text-sm mt-6 max-w-2xl mx-auto">
          Awards are granted on merit. Recognition reflects genuine performance and
          industry impact.
        </p>
      </section>

      {/* Fees */}
      <section className="p-8 sm:p-10 rounded-2xl bg-[#121212] border border-white/5">
        <SectionHeader icon={Star} eyebrow="Nomination Fees" title="Free to nominate." compact />
        <p className="text-white/60 leading-relaxed max-w-3xl">
          There is <span className="text-[#D4AF37] font-semibold">no submission fee</span> for
          the 2027 Travel Awards & TWBL Awards. Submit your organization at no cost —
          our transparent methodology ensures every nomination is evaluated purely on
          merit.
        </p>
      </section>

      {/* Dates */}
      <section>
        <SectionHeader icon={Calendar} eyebrow="Timeline" title="Key Dates" />
        <div className="space-y-3">
          {KEY_DATES.map((d) => (
            <div
              key={d.label}
              className="flex items-center justify-between gap-4 p-5 rounded-xl bg-[#121212] border border-white/5 hover:border-[#D4AF37]/30 transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center">
                  <Calendar className="w-5 h-5" />
                </div>
                <p className="text-white font-medium">{d.label}</p>
              </div>
              <p className="font-['Playfair_Display'] text-lg text-[#D4AF37] text-right">
                {d.date}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="text-center bg-gradient-to-br from-[#161616] via-[#101010] to-[#0A0A0A] border border-[#D4AF37]/20 rounded-3xl p-10 sm:p-14">
        <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80 mb-4 flex items-center justify-center gap-2">
          <Crown className="w-4 h-4" />
          Join the World&apos;s Finest Travel Brands
        </p>
        <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium text-white mb-4">
          Travel Awards & TWBL Awards 2027
        </h2>
        <p className="text-white/60 max-w-2xl mx-auto leading-relaxed mb-8">
          Recognizing Excellence. Inspiring Travel. Celebrating the World&apos;s Best.
          If your organization delivers exceptional experiences, now is the time to be
          recognized.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Button
            onClick={handleSubmitClick}
            className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A] h-12 px-8 text-base"
            data-testid="closing-nominate-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            {isAuthenticated ? 'Submit Your Nomination Today' : 'Sign Up to Nominate'}
          </Button>
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ icon: Icon, eyebrow, title, compact = false }) {
  return (
    <div className={compact ? 'mb-4' : 'mb-10'}>
      <div className="flex items-center gap-3 mb-4">
        {Icon && <Icon className="w-5 h-5 text-[#D4AF37]" />}
        <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80">{eyebrow}</p>
        <div className="flex-1 h-px bg-gradient-to-r from-[#D4AF37]/30 to-transparent" />
      </div>
      <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium text-white">
        {title}
      </h2>
    </div>
  );
}

